import React, { useState, useContext, useCallback, useEffect } from "react";
import { AccountContext } from "./../../store/AccountContext";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
  dialogTitleClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link, useNavigate, useParams } from "react-router-dom";
import usePromise from "react-use-promise";

import {
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";

import visa from "payment-icons/min/flat/visa.svg";
import mastercard from "payment-icons/min/flat/mastercard.svg";
import amex from "payment-icons/min/flat/amex.svg";
import discover from "payment-icons/min/flat/discover.svg";
import { useToasts } from "react-toast-notifications";

/**
 * PM5 T2 (money path, batch "myaccount-membership") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * PAYMENTS + SUBSCRIPTIONS ESCALATION CLASS — this is the live routed
 * add/update/remove-card screen (`/myaccount/payment-method/:action`), and it
 * runs the SCA `confirmCardPayment` flow. Styling-only: no handler, condition,
 * state value, prop or DOM node moved. `ELEMENT_OPTIONS` is Stripe's own
 * iframe styling API (it is NOT CSS and cannot be reached from this document),
 * so it is left byte-for-byte alone. The three Element mount nodes keep their
 * v4 box declarations verbatim, so the iframe each one hosts is measured
 * against the same box it was before.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `background`, `myAccountContainer`,
 * `myAccountGridContainer`, `myAccountTitle`, `cardValidation`,
 * `defaultPaymentMethodText` and `defaultPaymentMethodLabel` were declared but
 * never referenced from this file's JSX. `inputLabel` was an empty object —
 * JSS emitted an empty rule. The dialog's "Ok" <Button> carried
 * `className={classes.dialogButtonSml}`, a key that does not exist in this
 * file's `useStyles` object, so it resolved to `className={undefined}`.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements / third-party components that carry a fixed
 * style. None of these accept an `sx` prop, so `styled()` is the port.
 * ---------------------------------------------------------------------- */

// was: useStyles().activeBreadcrumb — a react-router <Link>.
const ActiveBreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
}));

// was: useStyles().defaultPaymentMethodExpDate — a bare <span>.
const ExpDateSpan = styled("span")(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  fontWeight: 400,
  fontSize: "0.9rem",
}));

// was: useStyles().defaultPaymentMethodCardBrand — a bare <img>.
const CardBrandImg = styled("img")({
  width: "50px",
  maxWidth: "50px",
  paddingRight: "9px",
  paddingTop: "6px",
});

// was: useStyles().cardElementLabel — a bare <label>.
const CardElementLabel = styled("label")({
  color: "#656565",
  fontSize: "1rem",
  fontWeight: 500,
  fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
});

// was: useStyles().inputFieldTight, on the Stripe CardNumberElement mount node.
const CardNumberField = styled(CardNumberElement)({
  padding: "12px",
  border: "1px solid #a5a5a5",
  backgroundColor: "#f7f7f7",
  margin: "4px 0 8px 0",
  fontSize: "16px",
  borderRadius: "5px",
});

/**
 * was: `className={`${classes.inputField} ${classes.bottomPaymentCreds}`}` on
 * the Stripe CardExpiryElement mount node.
 *
 * Guide §2, "Two `makeStyles` keys on one element": both classes are
 * single-class specificity, so v4's winner was JSS sheet order — the order of
 * the keys in the `useStyles` object — and `bottomPaymentCreds` is declared
 * AFTER `inputField`. So the rendered box has `margin: 4px 0 0 0`, not
 * `4px 0 15px 0`. Reproduced by listing `margin` first and `marginBottom`
 * after it; do not "tidy" that order.
 */
const CardExpiryField = styled(CardExpiryElement)({
  padding: "12px",
  border: "1px solid #a5a5a5",
  backgroundColor: "#f7f7f7",
  margin: "4px 0 15px 0",
  fontSize: "16px",
  borderRadius: "5px",
  marginBottom: 0,
});

// was: useStyles().inputField, on the Stripe CardCvcElement mount node.
const CardCvcField = styled(CardCvcElement)({
  padding: "12px",
  border: "1px solid #a5a5a5",
  backgroundColor: "#f7f7f7",
  margin: "4px 0 15px 0",
  fontSize: "16px",
  borderRadius: "5px",
});

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ v4's `<Grid container spacing={2}>` was
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaces that with `gap` on the container and no padding on the items
 * (guide §2). Carry v4's own declarations verbatim: omit `spacing` (v9 default
 * 0 ⇒ `gap: 0px`) and give each item the 8px padding itself.
 */
const v4GridContainerSx = { width: "calc(100% + 16px)", margin: "-8px" };
const v4GridItemSx = { padding: "8px" };

/**
 * ♦ A `<Grid item>` whose parent is another Grid ITEM, not a Grid container —
 * v9 publishes `--Grid-parent-columns` only from a container's `> *` rule, so
 * the `size` `calc()` would be invalid here. Guide §2 ("Grid without a
 * container"): port it as a Box carrying the literal v4 declarations. v4's
 * `sm`/`md` copies of `grid-xs-12` are the same 100%, so one unconditional set
 * reproduces every breakpoint.
 */
const orphanGridItemFullSx = {
  boxSizing: "border-box",
  flexGrow: 0,
  flexBasis: "100%",
  maxWidth: "100%",
};

/**
 * ♦ NEW DELTA (guide §2). v4's `.MuiGrid-container` declared `width: 100%`;
 * v9's Grid container has NO width rule at all — `generateGridStyles` emits
 * only `display: flex` / `flex-wrap` / `gap`. Harmless for a block-level
 * container, but a container nested DIRECTLY inside another container is a
 * flex item, and `width: auto` makes it shrink-to-fit. Measured in Chromium
 * on the exact emitted CSS: 800px in v4, 32px in v9, restored to 800px by
 * this rule. Restated verbatim on every converted container.
 */
const v4GridContainerWidthSx = { width: "100%" };

// was: useStyles().breadcrumbContainer
const breadcrumbContainerSx = (theme) => ({
  margin: theme.spacing(1, 0, 2, 0),
});

// was: useStyles().disabledBreadcrumb
const disabledBreadcrumbSx = { color: "#777777" };

// was: useStyles().myAccountSubTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted query is identical.
const myAccountSubTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  textAlign: "left",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.4rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().myAccountThirdTitle
const myAccountThirdTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.2rem",
  textAlign: "left",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
    lineHeight: "1.2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.2rem",
    lineHeight: "1.2rem",
  },
});

// was: useStyles().bodyText
const bodyTextSx = (theme) => ({ color: theme.palette.background.ffNavy });

// was: useStyles().bodyTextSml
const bodyTextSmlSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "0.9rem",
});

// was: useStyles().cardContainer
const cardContainerSx = (theme) => ({
  // plus v4's `.MuiGrid-container` width (see v4GridContainerWidthSx above)
  width: "100%",
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
});

// was: useStyles().validationError
const validationErrorSx = (theme) => ({ margin: theme.spacing(2, 0) });

/**
 * was: `<Box display="flex" alignItems="center" className={classes.
 * defaultPaymentMethodContainer}>`.
 *
 * Guide §2, "`makeStyles` `className` alongside `Box` system props": both
 * collapse into one `sx`, and emotion serialises in key insertion order, so
 * the ex-system-props go FIRST and the ex-`className` rules after them —
 * matching v4, where the file's own JSS sheet injected later than Box's and
 * therefore won any shared key.
 */
const defaultPaymentMethodContainerSx = (theme) => ({
  display: "flex",
  alignItems: "center",
  border: "1px solid #a5a5a5",
  borderRadius: "5px",
  backgroundColor: "#dedede",
  padding: theme.spacing(1.2),
  marginBottom: theme.spacing(3.5),
});

// was: useStyles().defaultPaymentCardNumber
const defaultPaymentCardNumberSx = (theme) => ({
  textAlign: "left",
  color: theme.palette.background.ffNavy,
  fontWeight: 500,
});

// was: useStyles().defaultPaymentMethodChange
const defaultPaymentMethodChangeSx = (theme) => ({
  color: theme.palette.background.ffGreen,
  fontWeight: 500,
  textDecoration: "underline",
  "&:hover": {
    cursor: "pointer",
  },
});

// was: useStyles().midPaymentCreds, merged with the Grid item padding above
// (guide §2 — `Grid`'s `sx` has no array form, so the layers merge into one
// object; the two rules share no property, so order is not load-bearing here).
const midPaymentCredsItemSx = (theme) => ({
  padding: "8px",
  marginTop: "4px",
  [theme.breakpoints.up("sm")]: {
    marginTop: 0,
  },
});

// was: useStyles().paymentButton, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted `default` and
// made `primary` the default — and this theme's `primary.main` IS the lime that
// `typography.button` paints the button background with, so an uncompensated
// port renders an invisible lime-on-lime "Add/Update/Remove Payment Method"
// button.
const paymentButtonSx = (theme) => ({
  margin: "10px 0",
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginTop: 0,
    width: "auto",
  },
  color: "text.primary",
});

// The same PARITY RESTORE for the dialog's "Ok" <Button>, which carried no
// effective v4 class (see the header note on `dialogButtonSml`). themeV5's
// `outlined` slot only restates `color` on `:hover`, so `variant="outlined"`
// needs it too — same call as `Overview.js`.
const buttonColourRestoreSx = { color: "text.primary" };

// was: useStyles().footerDivider
const footerDividerSx = {
  height: "1px",
  backgroundColor: "#3c3b3b",
  margin: "5% 0 2% 0",
};

// was: useStyles().tableText
const tableTextSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  "& span": {
    fontWeight: 500,
  },
});

/**
 * was: useStyles().autoRenewDialog — a §1.2b hit on the Dialog root, reaching
 * into the DialogTitle it renders. Re-pointed at `dialogTitleClasses.root`
 * (`FfV5DialogTitle-root`) rather than the hand-written v4 string, which the
 * `FfV5` prefix would make match nothing. Same shape as `Overview.js`.
 */
const autoRenewDialogSx = {
  justifyContent: "center",
  textAlign: "center",
  [`& .${dialogTitleClasses.root}`]: {
    backgroundColor: "#171717",
  },
};

/**
 * was: useStyles().autoRenewDialogTitle, whose rule was
 * `"& .MuiTypography-root": { lineHeight: "1.6rem", color: "#eee" }`.
 *
 * ♦ Guide §2 (T2/myaccount-profile row). In v4 `DialogTitle` rendered
 * `<div class="MuiDialogTitle-root"><h2 class="MuiTypography-root">…</h2></div>`,
 * so that DESCENDANT selector styled the inner Typography. In v5+ `DialogTitle`
 * **is** `styled(Typography)` — the h2 is the root itself, there is no
 * descendant Typography, and the ported selector would match nothing. The two
 * declarations therefore move onto the DialogTitle's own `sx`: same element,
 * same rendering.
 */
const autoRenewDialogTitleSx = {
  lineHeight: "1.6rem",
  color: "#eee",
};

export default function ChangePaymentMethod() {
  const params = useParams();
  console.log("PROPS", params.action);

  const paymentMethodAction = params.action ? params.action : "add";
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { addToast } = useToasts();

  // Account Context
  const {
    updating,
    handleCardSubmit,
    resetErrors,
    getMembershipDetails,
    validateLogin,
    updateError,
    userProfile,
    handleRemovePaymentMethod,
    handleCancelMembership,
  } = useContext(AccountContext);

  useEffect(() => {
    resetErrors();
    // eslint-disable-next-line
  }, []);

  // State
  const [authConfirming, setAuthConfirming] = useState(false);
  const [authConfirmed, setAuthConfirmed] = useState(false);
  const [authConfirmError, setAuthConfirmError] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(Date.now());
  const [membershipDetails, , state] = usePromise(getMembershipDetails, [
    reloadTrigger,
  ]);
  const [cardInputError, setCardInputError] = useState(null);

  const loading = state === "pending";

  // eslint-disable-next-line
  const reload = () => setReloadTrigger(Date.now());

  const clearErrors = (event) => {
    setCardInputError("");
    resetErrors();
  };

  const handleAuthConfirmDialogClose = () => {
    if (authConfirmError) {
      window.location.reload();
    } else {
      validateLogin();
      navigate("/myaccount/overview");
    }
  };

  const ELEMENT_OPTIONS = {
    style: {
      base: {
        fontSize: "16px",
        "::placeholder": {
          color: "#ccc",
        },
      },
      invalid: {
        color: "#b92020",
      },
    },
  };

  const onSubmitRemovePaymentMethod = async () => {
    try {
      const removePaymentMethod = await handleRemovePaymentMethod(
        userProfile.id,
      );

      if (removePaymentMethod) {
        if (userProfile && userProfile.productAutoRenewing) {
          await handleCancelMembership();
        }
        addToast("Payment Method Removed.", {
          appearance: "success",
        });
        validateLogin();
        reload();
      } else {
        addToast("Error - there was a problem", {
          appearance: "error",
        });
      }
    } catch (e) {
      addToast(e.toString(), {
        appearance: "error",
      });
    } finally {
      navigate("/myaccount/overview");
    }
  };

  const onSubmitUpdatePaymentMethod = useCallback(
    async (event) => {
      event.preventDefault();

      const cardElement = elements.getElement(CardNumberElement);
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (error) {
        console.error("Error creating payment method", error);
        setCardInputError(error.message);
        return;
      }

      if (paymentMethod) {
        try {
          const updateCard = await handleCardSubmit(paymentMethod.id);

          if (updateCard.success) {
            if (updateCard.authRequired) {
              setAuthConfirming(true);
              setAuthConfirmed(false);
              setAuthConfirmError(false);
              await stripe
                .confirmCardPayment(updateCard.clientSecret, {
                  payment_method: paymentMethod.id,
                })
                .then(() => {
                  setAuthConfirmed(true);
                  setAuthConfirmError(false);
                })
                .catch((error) => {
                  setAuthConfirmed(false);
                  setAuthConfirmError(true);
                })
                .finally(() => {
                  setAuthConfirming(false);
                });
            } else {
              addToast(
                paymentMethodAction === "add"
                  ? "Payment Method Successfully Added"
                  : paymentMethodAction === "update"
                    ? "Payment Method Successfully Updated"
                    : "",
                {
                  appearance: "success",
                },
              );
              validateLogin();
              reload();
              navigate("/myaccount/overview");
            }
          } else {
            addToast(updateCard.error, {
              appearance: "error",
            });
          }
        } catch (e) {
          console.log(e);
          addToast("Error:" + e, {
            appearance: "error",
          });
        }
      }
    },
    [
      addToast,
      elements,
      stripe,
      handleCardSubmit,
      validateLogin,
      navigate,
      paymentMethodAction,
    ],
  );

  return (
    <Grid container sx={v4GridContainerWidthSx}>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbContainerSx}>
          <ActiveBreadcrumbLink to="/myaccount/overview">
            Overview
          </ActiveBreadcrumbLink>
          <Typography sx={disabledBreadcrumbSx}>
            {paymentMethodAction === "update"
              ? "Update"
              : paymentMethodAction === "add"
                ? "Add"
                : paymentMethodAction === "remove"
                  ? "Remove"
                  : ""}{" "}
            Payment Method
          </Typography>
        </Breadcrumbs>
      </Grid>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Typography variant="h2" sx={myAccountSubTitleSx}>
          {paymentMethodAction === "update"
            ? "Update"
            : paymentMethodAction === "add"
              ? "Add"
              : paymentMethodAction === "remove"
                ? "Remove"
                : ""}{" "}
          Payment Method
        </Typography>
        <Typography variant="body1" sx={bodyTextSx}>
          {paymentMethodAction === "update"
            ? "Want to update the credit/debit card that we have on file? Provide the new details here."
            : paymentMethodAction === "add"
              ? "Provide the new details here"
              : paymentMethodAction === "remove"
                ? "Are you sure you wish to remove your saved payment method?"
                : ""}{" "}
        </Typography>
      </Grid>
      {!loading && (
        <Grid container sx={cardContainerSx}>
          {!updating && (
            <>
              <Grid container sx={v4GridContainerSx}>
                <Grid size={{ xs: 12, sm: 8, md: 6 }} sx={v4GridItemSx}>
                  {/* Current Card / Payment Method Details */}
                  {(paymentMethodAction === "update" ||
                    paymentMethodAction === "remove") &&
                    membershipDetails.defaultPaymentMethod && (
                      <>
                        <Typography variant="h3" sx={myAccountThirdTitleSx}>
                          Current Card Details
                        </Typography>
                        <Box sx={defaultPaymentMethodContainerSx}>
                          <Box sx={{ flex: "1", alignItems: "center" }}>
                            <Typography
                              variant="body1"
                              sx={defaultPaymentCardNumberSx}
                            >
                              **** **** ****{" "}
                              {membershipDetails.defaultPaymentMethod.last4}
                              <br />
                              <ExpDateSpan>
                                Exp:{" "}
                                {
                                  membershipDetails.defaultPaymentMethod.exp
                                    .month
                                }{" "}
                                /{" "}
                                {
                                  membershipDetails.defaultPaymentMethod.exp
                                    .year
                                }
                              </ExpDateSpan>
                            </Typography>
                          </Box>
                          <Box sx={{ alignItems: "center" }}>
                            <Typography
                              variant="body1"
                              sx={defaultPaymentMethodChangeSx}
                            >
                              {membershipDetails.defaultPaymentMethod
                                .cardBrand === "visa" && (
                                <CardBrandImg
                                  src={visa}
                                  alt={
                                    membershipDetails.defaultPaymentMethod
                                      .cardBrand
                                  }
                                />
                              )}
                              {membershipDetails.defaultPaymentMethod
                                .cardBrand === "mastercard" && (
                                <CardBrandImg
                                  src={mastercard}
                                  alt={
                                    membershipDetails.defaultPaymentMethod
                                      .cardBrand
                                  }
                                />
                              )}
                              {membershipDetails.defaultPaymentMethod
                                .cardBrand === "amex" && (
                                <CardBrandImg
                                  src={amex}
                                  alt={
                                    membershipDetails.defaultPaymentMethod
                                      .cardBrand
                                  }
                                />
                              )}
                              {membershipDetails.defaultPaymentMethod
                                .cardBrand === "discover" && (
                                <CardBrandImg
                                  src={discover}
                                  alt={
                                    membershipDetails.defaultPaymentMethod
                                      .cardBrand
                                  }
                                />
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </>
                    )}

                  {(paymentMethodAction === "add" ||
                    paymentMethodAction === "update") && (
                    <>
                      <Typography variant="h3" sx={myAccountThirdTitleSx}>
                        New Card Details
                      </Typography>
                      {updateError && (
                        <>
                          {updateError !== "undefined" && (
                            <Alert
                              sx={validationErrorSx}
                              severity="error"
                              variant="filled"
                            >
                              {updateError}
                            </Alert>
                          )}
                        </>
                      )}
                      {cardInputError && (
                        <>
                          {cardInputError !== "undefined" && (
                            <Alert
                              sx={validationErrorSx}
                              severity="error"
                              variant="filled"
                            >
                              {cardInputError}
                            </Alert>
                          )}
                        </>
                      )}
                      <Box
                        sx={orphanGridItemFullSx}
                        style={{ marginBottom: "15px" }}
                      >
                        <CardElementLabel htmlFor="cardNumber">
                          Card Number
                        </CardElementLabel>
                        <CardNumberField
                          id="cardNumber"
                          onFocus={clearErrors}
                          options={ELEMENT_OPTIONS}
                        />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        ></div>
                      </Box>
                      <Grid container sx={v4GridContainerSx}>
                        <Grid
                          size={{ xs: 6, sm: 6, md: 6 }}
                          sx={midPaymentCredsItemSx}
                        >
                          <CardElementLabel htmlFor="expiry">
                            Card Expiration
                          </CardElementLabel>
                          <CardExpiryField
                            id="expiry"
                            onFocus={clearErrors}
                            options={ELEMENT_OPTIONS}
                          />
                        </Grid>
                        <Grid
                          size={{ xs: 6, sm: 6, md: 6 }}
                          sx={midPaymentCredsItemSx}
                        >
                          <CardElementLabel htmlFor="cvc">CVC</CardElementLabel>
                          <CardCvcField
                            id="cvc"
                            onFocus={clearErrors}
                            options={ELEMENT_OPTIONS}
                          />
                        </Grid>
                      </Grid>

                      <Button
                        onClick={onSubmitUpdatePaymentMethod}
                        sx={paymentButtonSx}
                        // disabled={!stripe || !Subscription.id || submittingButton}
                        // fullWidth={true}
                      >
                        {paymentMethodAction === "update"
                          ? "Update"
                          : paymentMethodAction === "add"
                            ? "Add"
                            : ""}{" "}
                        Payment Method
                      </Button>
                      <Divider sx={footerDividerSx} />
                      <Typography variant="body1" sx={bodyTextSmlSx}>
                        Tip! Don&apos;t want to miss out, ensure your Auto Renew
                        is turned on.
                      </Typography>
                    </>
                  )}
                  {paymentMethodAction === "remove" &&
                    membershipDetails.defaultPaymentMethod && (
                      <>
                        <Button
                          onClick={onSubmitRemovePaymentMethod}
                          sx={paymentButtonSx}
                        >
                          Remove Payment Method
                        </Button>
                        <Divider sx={footerDividerSx} />
                        <Typography variant="body1" sx={bodyTextSmlSx}>
                          You can always add a new Payment Method in the future.
                        </Typography>
                      </>
                    )}
                </Grid>
              </Grid>
            </>
          )}
          {updating && (
            <Box sx={{ m: 5 }} align="center">
              <CircularProgress variant="indeterminate"></CircularProgress>
              <Typography variant="body2" sx={bodyTextSmlSx}>
                <br />
                {paymentMethodAction === "update"
                  ? "Updating"
                  : paymentMethodAction === "add"
                    ? "Adding"
                    : paymentMethodAction === "remove"
                      ? "Removing"
                      : ""}{" "}
                Payment Method. Please do not refresh your browser.
              </Typography>
            </Box>
          )}
        </Grid>
      )}
      {loading && (
        <Grid container sx={v4GridContainerWidthSx}>
          <Grid size={{ xs: 12, sm: 8, md: 6 }}>
            <Box sx={{ m: 5 }} align="center">
              <CircularProgress variant="indeterminate"></CircularProgress>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Payment Authentication Modal */}
      <Dialog
        fullWidth={true}
        aria-labelledby="customized-dialog-title"
        open={authConfirming || authConfirmed || authConfirmError}
        sx={autoRenewDialogSx}
        onClose={handleAuthConfirmDialogClose}
      >
        <DialogTitle id="customized-dialog-title" sx={autoRenewDialogTitleSx}>
          Card Authorized
        </DialogTitle>
        {/* `dividers` is set, so the guide §2 `DialogContent` padding delta does
            NOT apply here: v4's `dividers` slot and v9's `dividers` variant both
            declare `padding: 16px 24px`, and v9's `.MuiDialogTitle-root + &
            { paddingTop: 0 }` rule is gated on `!dividers`. Nothing to
            compensate — same call as `Overview.js`'s five dialogs. */}
        <DialogContent dividers>
          {authConfirming ? (
            <CircularProgress
              variant="indeterminate"
              style={{ alignSelf: "center", marginTop: 50, marginBottom: 50 }}
            ></CircularProgress>
          ) : authConfirmed ? (
            <Typography variant="body1" sx={tableTextSx}>
              Your card has been successfully authorized, any pending payments
              on your account will be processed within the next few minutes.
            </Typography>
          ) : (
            <Typography variant="body1" sx={tableTextSx}>
              We had problems authorizing your card. Please try again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Grid container sx={v4GridContainerSx}>
            <Grid size={{ xs: 12 }} sx={v4GridItemSx} align="center">
              <Button
                onClick={handleAuthConfirmDialogClose}
                sx={buttonColourRestoreSx}
                variant="outlined"
              >
                Ok
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
